import { FormEvent, useCallback, useState, useEffect, forwardRef, ReactElement, Ref } from "react";
// eslint-disable-next-line no-restricted-imports
import { Accordion } from "@chakra-ui/react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Slide from "@mui/material/Slide";
import Typography from "@mui/material/Typography";
import type { TransitionProps } from "@mui/material/transitions";
import { useTranslation } from "next-i18next";
import { EnumLeverageProductsIndication } from "src/api/client/extended-search.api";
import { IKeyFigureGroup, ITags, ITagsData } from "src/api/server/extended-search";
import { Button } from "src/components/base/button";
import { Switch } from "src/components/common/switch/switch";
import { MobileSearchFilterCloseButton } from "src/components/pages/search/components/filter-section/mobile-search-filter/mobile-search-filter-close-button";
import { InvestableInCountrySwitch } from "src/components/pages/search/components/investable-in-country-switch";
import { ISearchRequestParamsOmitCountry } from "src/components/pages/search/search.types";
import { handleIsInvestable } from "src/components/pages/search/store/actions";
import { useSearchStore } from "src/components/pages/search/store/search-store";
import { useNumberFormatter } from "src/hooks/formatters/use-number-formatter";
import { EnumCountryKey } from "src/types/common-enum";
import { useRouterLocale } from "src/utils/router/use-router-locale";
import { Button as CustomButton } from "../button";
import { KeyFiguresFilter } from "./key-figures-filter";
import { PopularFilters } from "./popular-filters";
import { TagsFilter } from "./tags-filter";

const Transition = forwardRef(function Transition(props: TransitionProps & { children: ReactElement }, ref: Ref<unknown>) {
    return <Slide direction="down" ref={ref} {...props} />;
});

interface IMobileSearchFilterProps {
    isOpen: boolean;
    isLoading: boolean;
    resultCount: number | null;
    tagsData: ITagsData;
    popularKeyFiguresGroup: IKeyFigureGroup;
    keyFiguresGroups: ReadonlyArray<IKeyFigureGroup>;
    popularTags: ITags;
    onClose: () => void;
    onReset: () => void;
    onSearch: (params: ISearchRequestParamsOmitCountry) => void;
}

export const MobileSearchFilter = ({
    isOpen,
    isLoading,
    resultCount,
    tagsData,
    popularKeyFiguresGroup,
    keyFiguresGroups,
    popularTags,
    onClose,
    onReset,
    onSearch,
}: IMobileSearchFilterProps) => {
    const { t } = useTranslation(["extended-search", "common"]);
    const [currentResultCount, setCurrentResultCount] = useState<number | null>(null);
    const { country } = useRouterLocale();
    const { formatNumber } = useNumberFormatter();

    const { query, isInvestable, brokersIds, tags, keyFigures, isAtInvestable, isChInvestable, leverageProductsIndication } = useSearchStore();
    const isAnyFilterApplied = [
        query,
        country === EnumCountryKey.INT ? isInvestable : !isInvestable,
        brokersIds.length,
        tags.length,
        keyFigures.length,
        country === EnumCountryKey.AT ? !isAtInvestable : isAtInvestable,
        country === EnumCountryKey.CH ? !isChInvestable : isChInvestable,
        leverageProductsIndication !== EnumLeverageProductsIndication.AllWikifolios,
    ].some(Boolean);

    useEffect(() => {
        if (resultCount !== currentResultCount) {
            setCurrentResultCount(resultCount);
        }
    }, [currentResultCount, resultCount]);

    const isInvestableFilterVisible = country !== EnumCountryKey.INT;

    const getShowResultButtonText = useCallback(() => {
        if (resultCount !== null) {
            if (resultCount === 1) {
                return t("buttons.show-1-result");
            } else {
                return t("buttons.show-XX-results", { resultCount: formatNumber(resultCount) });
            }
        } else {
            return t("buttons.show-all-results");
        }
    }, [formatNumber, resultCount, t]);

    const onChangeInvestable = (event: FormEvent<HTMLInputElement>) => {
        const isChecked = event.currentTarget.checked;
        handleIsInvestable({ isChecked, onSearch });
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            fullScreen
            scroll="paper"
            slots={{ transition: Transition }}
            slotProps={{
                paper: {
                    sx: { m: 0, borderRadius: 0, maxWidth: "100%", width: "100vw", position: "relative", backgroundColor: "gray.50" },
                },
            }}
        >
            <Box
                sx={{
                    px: 3,
                    py: 1,
                    position: "relative",
                    minHeight: "56px",
                    borderBottom: "1px solid",
                    borderColor: "gray.100",
                    backgroundColor: "white",
                }}
            >
                {isAnyFilterApplied && (
                    <Button variant="ghost-underline" size="sm" onClick={onReset} sx={{ px: 0, position: "absolute" }}>
                        {t("buttons.reset-search-criteria")}
                    </Button>
                )}
                <MobileSearchFilterCloseButton
                    onClose={onClose}
                    sx={{
                        position: "absolute",
                        right: "16px",
                        top: "8px",
                    }}
                />
            </Box>
            <DialogContent
                sx={{
                    p: 0,
                    backgroundColor: "gray.50",
                    flex: 1,
                    overflowY: "auto",
                }}
            >
                {isInvestableFilterVisible && (
                    <Box ml={-1} mb={1} p={3} display="flex" flexDirection="column" gap={4} bgcolor="white">
                        <Box
                            display="flex"
                            alignItems="center"
                            width="auto"
                            flexDirection="row-reverse"
                            justifyContent="space-between"
                            fontWeight={600}
                        >
                            <Switch isChecked={isInvestable} onChange={onChangeInvestable} />
                            <Typography component="label" sx={{ mb: 0, ml: 1, fontWeight: "inherit" }}>
                                {t("labels.investable-wikifolios-only", { ns: "common" })}
                            </Typography>
                        </Box>
                        <InvestableInCountrySwitch
                            styles={{ flexDirection: "row-reverse", justifyContent: "space-between", fontWeight: "semibold" }}
                            onSearch={onSearch}
                        />
                    </Box>
                )}
                <Box display="flex" flexDirection="column" gap={1} px={0} py={0}>
                    <Accordion display="flex" flexDirection="column" gap={1} allowMultiple>
                        <PopularFilters popularKeyFiguresGroup={popularKeyFiguresGroup} tagsData={popularTags} onSearch={onSearch} />
                        <KeyFiguresFilter keyFiguresGroups={keyFiguresGroups} numberOfSelectedKeyFigures={keyFigures.length} onSearch={onSearch} />
                        <TagsFilter tagsData={tagsData} numberOfSelectedTags={tags.length} onSearch={onSearch} />
                    </Accordion>
                </Box>
            </DialogContent>
            <Box
                sx={{
                    p: 3,
                    borderTop: "1px solid",
                    borderColor: "gray.100",
                    backgroundColor: "white",
                }}
            >
                <CustomButton w="100%" colorScheme="gray" onClick={onClose} fontWeight="bold" isLoading={isLoading}>
                    {getShowResultButtonText()}
                </CustomButton>
            </Box>
        </Dialog>
    );
};
